import { useState } from "react";
import { View, Text } from "react-native";
import { gql, useQuery } from "@apollo/client";
import ScreenContainer from "../../components/ScreenContainer";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import AvatarProfile from "../../components/AvatarProfile";
import useCurrentUser from "../../hooks/useCurrentUser";
import useDebounce from "../../hooks/useDebounce";

const USER_TO_ASSUME_QUERY = gql`
  query UserToAssume {
    userToAssume: currentMember {
      id: memberNumber
      firstName
      lastName
      primaryEmail
      profilePhoto
    }
  }
`;

type UserToAssumeQuery = {
  userToAssume: {
    id: string;
    firstName: string;
    lastName: string;
    primaryEmail: string;
    profilePhoto: string;
  };
};

const AssumeUserScreen = () => {
  const [memberNumber, setMemberNumber] = useState<string>("");
  const debouncedMemberNumber = useDebounce(memberNumber, 300);
  const { data } = useQuery<UserToAssumeQuery>(USER_TO_ASSUME_QUERY, {
    context: { headers: { "X-PGA-Impersonation": debouncedMemberNumber.trim() } },
    skip: !debouncedMemberNumber || debouncedMemberNumber.trim().length === 0,
  });
  const { memberNumber: currentUserMemberNumber, assumeUser } = useCurrentUser();

  const userToAssume = data?.userToAssume?.id && data?.userToAssume?.id !== currentUserMemberNumber ? data.userToAssume : null;

  return (
    <ScreenContainer>
      <View className="px-2">
        <Text className="text-small-normal font-400 pb-2">Assume the identity of another user by entering their PGA ID.</Text>
        <Text className="text-small-normal font-700 pb-2">
          Any changes you make while impersonating will be applied to the user's account.
        </Text>
        <Text className="text-small-normal font-400">Be sure to click "I'm Done" when you're finished.</Text>
        <TextInput className="my-4" label="PGA ID" value={memberNumber} onChangeText={setMemberNumber} />
        {userToAssume && (
          <View className="flex-row mt-2 mb-6 px-2 items-center">
            <AvatarProfile
              profilePhoto={userToAssume.profilePhoto}
              firstName={userToAssume.firstName}
              lastName={userToAssume.lastName}
            />
            <View className="ml-4 items-start">
              <Text className="text-title-4 font-700">
                {userToAssume.firstName} {userToAssume.lastName}
              </Text>
              <Text className="text-regular-normal font-400 mt-1">{userToAssume.primaryEmail}</Text>
            </View>
          </View>
        )}
        <Button
          mode="contained"
          onPress={() =>
            assumeUser({ memberNumber: userToAssume!.id, firstName: userToAssume!.firstName, lastName: userToAssume!.lastName })
          }
          disabled={!userToAssume}
        >
          Assume User
        </Button>
      </View>
    </ScreenContainer>
  );
};

export default AssumeUserScreen;
